import styled, { css } from 'styled-components';
import { forwardRef, KeyboardEvent } from 'react';
import { AVATAR_SIZES, Avatar } from 'ui/avatar';
import { useSelector } from 'react-redux';
import { body1, body2, spacing, colors, Overline } from 'ui/theme';
import { BUTTON_VARIANTS } from 'ui/button';
import { LinkButton } from 'ui/linkButton';

import {
  selectFirstName,
  selectLastName,
  selectUserAvatarUrl,
  selectProjects,
  selectEmail,
  selectUserProject,
} from 'core/userContext';

import { SecondaryOption } from './SecondaryOption';

const ProfileArea = styled.div`
  background-color: ${colors.grey1};
  padding: ${spacing.threeQuarter};
`;

const ProjectListArea = styled.div`
  padding: ${spacing.threeQuarter};
  ${Overline} {
    padding: ${spacing.half};
  }

  a {
    &:hover,
    &:focus {
      background: ${colors.purple10};
    }
  }
`;

const AvatarArea = styled.div`
  display: flex;
  align-items: center;
`;

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

const ButtonArea = styled.div`
  margin-top: ${spacing.half};
  display: flex;
  justify-content: space-around;
  a {
    display: flex;
  }
`;

const ProjectSelectionContainer = styled.div``;

const UserInfoArea = styled.div`
  margin-left: 7px;
  .name-text {
    ${body1};
  }

  .email-text {
    ${body2};
    color: ${colors.textSecondary};
  }
`;

const ProjectSecondaryOption = styled(SecondaryOption)<{ isSelected?: boolean }>`
  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${colors.grey5};
    `}
`;

interface ProjectSelectionProps {
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
}

export const ProjectSelection = forwardRef<HTMLDivElement, ProjectSelectionProps>(
  ({ onKeyDown }, listRef) => {
    const firstName = useSelector(selectFirstName);
    const lastName = useSelector(selectLastName);
    const userAvatarUrl = useSelector(selectUserAvatarUrl);
    const projects = useSelector(selectProjects);
    const currentProject = useSelector(selectUserProject);
    const email = useSelector(selectEmail);

    return (
      <ProjectSelectionContainer>
        <ProfileArea>
          <AvatarArea>
            <StyledAvatar
              firstName={firstName}
              lastName={lastName}
              imgSrc={userAvatarUrl}
              size={AVATAR_SIZES.MEDIUM}
              data-test="user-avatar"
            />
            <UserInfoArea>
              <div className="name-text">{`${firstName} ${lastName}`}</div>
              <div className="email-text">{email}</div>
            </UserInfoArea>
          </AvatarArea>
          <ButtonArea>
            <LinkButton to="/preferences" variant={BUTTON_VARIANTS.SECONDARY}>
              Account settings
            </LinkButton>

            {/* requires page reload to navigate to the login page after logging out, which `isAngular` provides with href tag */}
            <LinkButton isAngular to="/logout" variant={BUTTON_VARIANTS.SECONDARY}>
              Sign Out
            </LinkButton>
          </ButtonArea>
        </ProfileArea>
        <ProjectListArea>
          <Overline className="title">MY PROJECTS</Overline>
          <div ref={listRef} onKeyDown={onKeyDown}>
            {projects.map((project) => (
              <ProjectSecondaryOption
                key={project.id}
                href={`/workflows?projectId=${project.id}`}
                data-test="quick-nav-secondarylink-project"
                title={project.name}
                isSelected={currentProject?.id === project.id}
              />
            ))}
          </div>
        </ProjectListArea>
      </ProjectSelectionContainer>
    );
  },
);
